import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, Button, FormControl, Select, MenuItem, FormHelperText, InputLabel, Checkbox, ListSubheader, TextField } from '@mui/material';

RHFMultipleSelectField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  search: PropTypes.bool,
  selectAll: PropTypes.bool,
};

export default function RHFMultipleSelectField({ name,
  options,
  variant,
  label,
  disableOptions = [],
  width = 150,
  noOptionsText = 'No options available',
  // Opt-in: a search box at the top of the menu that narrows the options by label.
  search = false,
  // Opt-in: match RHFTextField's compact sizing (10px label, 12px value/items) so this sits
  // flush with text fields in the same filter bar. Default keeps the original 14px sizing.
  compact = false,
  // Opt-in: a "Select all / Clear" row at the top of the menu (acting on the options the search
  // shows) and an "N selected" summary once more than two are picked.
  selectAll = false,
  ...other }) {
  const { control } = useFormContext();
  const uniqueId = (new Date()).getTime();

  const [searchText, setSearchText] = useState('');
  const shownOptions = search && searchText
    ? options.filter((o) => String(o.label).toLowerCase().includes(searchText.toLowerCase()))
    : options;
  const noOptionsAvailable = shownOptions.length === 0;


  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const picked = Array.isArray(field.value) ? field.value : [];
        const shownValues = shownOptions.map((o) => o.value);
        const allShownPicked = shownValues.length > 0 && shownValues.every((v) => picked.includes(v));
        const labelOf = (v) => options.find((o) => o.value === v)?.label ?? v;
        return (
        <FormControl variant={variant || "filled"} error={!!error} size={compact ? 'small' : undefined} sx={{ minWidth: width, maxWidth: width }}>
          <InputLabel id={`demo-multiple-${uniqueId}`} sx={{ fontSize: compact ? 10 : 14 }}>{label}</InputLabel>
          <Select
            {...field}
            multiple
            labelId={`demo-multiple-${uniqueId}`}
            // value={typeof field?.value === 'number' && field?.value === null ? "ALL" : [...field.value]}
            value={Array.isArray(field.value) ? field.value : []}
            fullWidth={true}
            size='small'
            sx={compact ? { fontSize: 12 } : undefined}
            onClose={() => setSearchText('')}
            MenuProps={search ? { autoFocus: false } : undefined}
            {...(selectAll ? {
              renderValue: (selected) => (selected.length > 2
                ? (selected.length === options.length ? `All (${selected.length})` : `${selected.length} selected`)
                : selected.map(labelOf).join(', ')),
            } : {})}
            {...other}
          >
            {search && (
              <ListSubheader sx={{ pt: 1 }}>
                <TextField
                  size="small" fullWidth autoFocus placeholder="Search" value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  // Keep typing in the box instead of MUI's type-to-select jumping between items.
                  onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                />
              </ListSubheader>
            )}
            {selectAll && options.length > 0 && (
              <ListSubheader sx={{ top: search ? 48 : 0, lineHeight: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: '1px solid #eef0f3' }}>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>{picked.length} of {options.length} selected</Box>
                <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" disabled={allShownPicked || shownValues.length === 0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); field.onChange([...new Set([...picked, ...shownValues])]); }}
                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, py: 0.25 }}>
                    {searchText ? `Select ${shownValues.length} shown` : 'Select all'}
                  </Button>
                  <Button size="small" color="inherit" disabled={picked.length === 0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); field.onChange(searchText ? picked.filter((v) => !shownValues.includes(v)) : []); }}
                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, py: 0.25, color: 'text.secondary' }}>
                    {searchText ? 'Clear shown' : 'Clear'}
                  </Button>
                </Box>
              </ListSubheader>
            )}
            {/* <MenuItem value="">
              <em>None</em>
            </MenuItem> */}
            {noOptionsAvailable ? (
              <MenuItem disabled value="">
                <em>{noOptionsText}</em>
              </MenuItem>
            ) : (
              shownOptions.map((e, i) => (
                <MenuItem
                  key={i}
                  value={e.value}
                  sx={{ fontSize: compact ? 12 : 14 }}
                >
                  <Checkbox checked={field?.value?.includes(e.value)} />
                  {e.label}
                </MenuItem>
              ))
            )}

            {/* {options.map((e, i) => <MenuItem sx={{ fontSize: 14 }} key={i} value={e.value}>
              <Checkbox checked={field?.value?.includes(e.value)} />
              {e.label}
            </MenuItem>)} */}
          </Select>
          {(!compact || error) && <FormHelperText>{error?.message}</FormHelperText>}
        </FormControl>
        );
      }}
    />
  );
}
